import * as A from 'astronomy-engine';
import { effect, signal, untracked } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { services, type AppServices } from '../app';
import { DEG, type Vec3 } from '../lib/math';
import { DSO_EYE, EYE_MAX, eyeMagAt, eyeSeesAt, eyeSkyFor, type EyeSky } from '../render/nakedeye';
import { nightId } from '../live/nightsCore';
import {
  DARK_ENOUGH, MOON_TWILIGHT_MAX, NO_BOUNDS, ROUNDS, applyAnswer, cantSee, cantSeeTaps, checkAt, chooseCheckStars, distanceKm, eyeMag, eyeTonight,
  forgetCheck, gatherCheckStars, judgeCheck, limitNudge, markOffered, markReoffered, measuredSqm, moonTwilightShare, nudgeTonightTo, offerAgainWhy, offerDue,
  offeredAt, offeredPlaces, resetNudge, saveCheck, seeMore, settle, skyChecks, sqmForLimit, type AgainWhy, type Answer, type Bounds, type CheckStar,
  type Settled, type SkyNow, type TonightOnly,
} from '../live/skycheck';
import {
  calendarFilmOpen, storyOpen, darkOut, endPreview, goLive, introOpen, panel, place, pointingPrompt, previewFrom, reliving, selection, settings, showToast,
  simNow, timeLive, viewMode, onceOfferShown, whatToShow, type Selection } from '../state';
import { useDialog } from './chrome';
import { fmtDate, fmtNum, fmtTime, whereInSky } from './format';
import { guideOffer } from './Guide';
import { aligning, alignOfferShown } from '../live/align';
import { Icon } from './icons';
import { Quote } from './Quote';

// The darkness check: the person taps the faintest of a few ringed stars they can see, and the naked-eye view follows
// their eyes. Offered once per place, the first time Ahillah is used there after dark; always in Sky view; and again
// after two "I can't see it" taps in a night. The sums are in src/live/skycheck.ts.

// ---------- The sky on show ----------
/** The Sun and Moon in the sky on show, as the sky brightness model takes them. */
export function skyNow(s: AppServices): SkyNow {
  const e = s.engine;
  const alt = (dir: Vec3) => Math.asin(Math.max(-1, Math.min(1, e.frame.toEnu(dir)[2]))) / DEG;
  const sun = e.bodies.find((b) => b.id === 'Sun'), moon = e.bodies.find((b) => b.id === 'Moon');
  return { sunAlt: sun ? alt(sun.dir) : e.context?.sunAlt ?? -90, moonAlt: moon ? alt(moon.dir) : -90, moonMag: moon?.mag ?? -10 };
}

/** The eye's sky (render/nakedeye.ts) under a Sun and Moon, the Moon where the engine has it, with a nudge. */
function eyeSkyOf(s: AppServices, k: SkyNow, nudge: number): EyeSky {
  const set = settings.peek(), e = s.engine, moon = e.bodies.find((b) => b.id === 'Moon');
  const moonEnu: Vec3 = moon ? e.frame.toEnu(moon.dir) : [0, 0, -1];
  return eyeSkyFor(k.sunAlt, moonEnu, k.moonMag, set.bortle, measuredSqm(place.peek()) ?? (set.bortleAuto ? set.sqm : null), nudge);
}
/** The eye's limit overhead for the sky on show, with a nudge. */
const limitWith = (s: AppServices, nudge: number) => eyeSkyOf(s, skyNow(s), nudge).limit;
/**
 * Tonight's naked-eye limit overhead for the sky on show, tonight's nudge included: what Naked eye shows (anything
 * fainter as if overhead, as `eyeMagAt` has it, is left out). The engine's own, as it drew the last frame; worked out
 * the same way before the first.
 */
export const eyeLimitNow = (s: AppServices) => s.engine.context?.nelm ?? limitWith(s, limitNudge.peek());
/** The eye's sky on show, tonight's nudge included: the limit overhead and across the sky, as Naked eye draws it. */
export const eyeSkyNow = (s: AppServices) => s.engine.context?.eyeSky ?? eyeSkyOf(s, skyNow(s), limitNudge.peek());
/** Tonight's limit before any "I can't see it" or "I can see more". */
export const baseLimitNow = (s: AppServices) => limitWith(s, 0);

/**
 * What Naked eye says about a star, planet or deep-sky object on a card: how bright it looks where it is (as if
 * overhead, the air and the sky's glow there allowed for) and whether the eye sees it, by the same test that draws it
 * (render/nakedeye.ts). A deep-sky object not among the eye's (`DSO_EYE`) is never seen. Something out of reach only
 * for now says why: twilight (it would be seen in a dark sky) or its height (it would be seen higher up). Null where it
 * says nothing: Full sky, the Sun and Moon, satellites and the rest, anything below the horizon, and the sky by day.
 */
export function eyeVerdict(s: AppServices, sel: Selection): { eff: number; visible: boolean; until: 'darker' | 'higher' | null } | null {
  if (!settings.value.nakedEye || (sel.kind !== 'star' && sel.kind !== 'body' && sel.kind !== 'dso')) return null;
  const e = s.engine;
  let mag: number | null | undefined;
  if (sel.kind === 'star') mag = e.stars.mag[sel.index];
  else if (sel.kind === 'body') mag = sel.id === 'Sun' || sel.id === 'Moon' ? null : e.bodies.find((b) => b.id === sel.id)?.mag;
  else {
    const d = e.dsos[sel.index];
    if (!d) return null;
    mag = DSO_EYE[d.id] ?? Infinity;
  }
  if (mag == null || Number.isNaN(mag)) return null;
  const loc = e.locate(sel), sky = skyNow(s);
  if (!loc || loc.enu[2] <= 0 || sky.sunAlt > 0) return null;
  const eye = eyeSkyNow(s), [x, y, z] = loc.enu;
  const eff = eyeMagAt(mag, x, y, z, eye);
  if (eyeSeesAt(mag, x, y, z, eye)) return { eff, visible: true, until: null };
  const darker = sky.sunAlt > DARK_ENOUGH && eyeSeesAt(mag, x, y, z, eyeSkyOf(s, { ...sky, sunAlt: -30 }, limitNudge.peek()));
  // Higher up, in the same direction: 60° up.
  const r = Math.hypot(x, y) || 1, up = Math.sin(60 * DEG), across = Math.cos(60 * DEG) / r;
  const higher = z < Math.sin(30 * DEG) && eyeSeesAt(mag, x * across, y * across, up, eye);
  return { eff, visible: false, until: darker ? 'darker' : higher ? 'higher' : null };
}

/** The sky on show is the sky here and now (no other time, place or saved night). */
const hereAndNow = () => timeLive.value && !previewFrom.value && !reliving.value;

// ---------- Opening it ----------
export const darkCheckOpen = signal(false);
/** Start the darkness check: from the offer, Sky view, or "I can see more stars than this". */
export function startDarkCheck() {
  selection.value = null;
  panel.value = null;
  darkCheckOpen.value = true;
}

/** When the Sun is next `alt` degrees down, going down (ms), within a day. */
function sunDownTo(p: { lat: number; lon: number }, alt: number): number | null {
  try {
    return A.SearchAltitude(A.Body.Sun, new A.Observer(p.lat, p.lon, 0), -1, new Date(), 1, alt)?.date.getTime() ?? null;
  } catch {
    return null;
  }
}

// ---------- The check ----------
/** The view can be trusted against the real sky: by finger always; by pointing, only once the compass knows north. */
export const pointingReady = (s: AppServices) => viewMode.peek() !== 'ar' || s.engine.orientation.northKnown;
/** The sky on show is here and now, read without following it. */
const hereNow = () => timeLive.peek() && !previewFrom.peek() && !reliving.peek();

/**
 * Where, when and under what sky a check is asked: its answers count only while that still holds. `moonlit`: asked for
 * under a Moon too bright to tell the light pollution by, so the answer counts for tonight only.
 */
interface Session { lat: number; lon: number; at: number; moonlit: boolean }
/** A round left open this long is out of date: the stars have moved on, and so have the eyes. */
const STALE_MS = 15 * 60e3;
/** Moved this far (a new fix, another place chosen), it is another place for the check. */
const MOVED_KM = 0.2;

type Few = 'low' | 'moon' | 'zoom' | 'other' | 'bright';
type Step =
  | { k: 'finding'; round: number; centre: number }
  /** A round's question: its stars, the session it was asked in, and when. */
  | { k: 'ask'; round: number; centre: number; stars: CheckStar[]; session: Session; at: number }
  /** `tonight`: why the answer is kept for tonight only, not for the place (`judgeCheck`, or a bright Moon). */
  | { k: 'done'; found: number; count: number; tonight: TonightOnly | null }
  | { k: 'few'; why: Few }
  | { k: 'early'; at: number | null }
  /** Pointing, while the compass is still finding north. */
  | { k: 'north' }
  /** The question put away (Ahillah in the background, or moved to another place): it is asked afresh once Ahillah is
   *  in front again and the engine has drawn the sky for this place and this moment. */
  | { k: 'away' }
  | { k: 'elsewhere' }
  | { k: 'fail' };

/**
 * The engine's last frame is of this place and this moment. Just back from the background, or just moved, it is not
 * yet: its Sun and Moon are still those of the sky as it was.
 */
function freshSky(s: AppServices): boolean {
  const e = s.engine, f = e.frame, p = place.peek();
  return e.bodies.length > 0 && Math.abs(f.date.getTime() - simNow().getTime()) < 5000
    && Math.abs(f.observer.latitude - p.lat) < 1e-6 && Math.abs(f.observer.longitude - p.lon) < 1e-6;
}

/**
 * Whether the sky on show allows a question now: null if it does, else the step to show instead, with nothing written.
 * Another time or place on show; the engine not yet drawn for this place and moment; not yet dark (the Sun above 12°
 * down) or twilight drowning it; or, pointing, north not yet known. Asked before a question, before its stars are
 * chosen, and before an answer is taken or kept.
 */
function notNow(s: AppServices): Step | null {
  if (!hereNow()) return { k: 'elsewhere' };
  if (!freshSky(s)) return { k: 'away' };
  const sky = skyNow(s), p = place.peek();
  if (sky.sunAlt > DARK_ENOUGH) return { k: 'early', at: sunDownTo(p, DARK_ENOUGH) };
  // Twilight on its own too bright to tell the light pollution by: later tonight.
  if (moonTwilightShare({ ...sky, moonAlt: -90 }, sqmForLimit(baseLimitNow(s), sky)) > MOON_TWILIGHT_MAX) return { k: 'early', at: sunDownTo(p, -15) };
  if (!pointingReady(s)) return { k: 'north' };
  return null;
}

/**
 * Where the check can start (`notNow`), from the sky as the engine has just drawn it. A bright Moon does not stop a
 * check asked for, but its answer counts for tonight only (`moonlit`).
 */
function firstStep(s: AppServices): { step: Step; moonlit: boolean } {
  const why = notNow(s);
  if (why) return { step: why, moonlit: false };
  const sky = skyNow(s);
  // The light pollution Ahillah has for here now (the model's limit taken back to it).
  const moonlit = moonTwilightShare(sky, sqmForLimit(baseLimitNow(s), sky)) > MOON_TWILIGHT_MAX;
  return { step: { k: 'finding', round: 1, centre: eyeLimitNow(s) }, moonlit };
}

/** The check's card as the page lays it out: its box, and whether the CSS puts it on the right (`--dc-side`: the phone
 *  on its side) rather than at the bottom. */
function cardLayout(el: HTMLElement | null): { box: { left: number; top: number; width: number }; side: boolean } | null {
  if (!el) return null;
  return { box: el.getBoundingClientRect(), side: typeof getComputedStyle === 'function' && getComputedStyle(el).getPropertyValue('--dc-side').trim() === '1' };
}

/** The ringed stars for a round, in the sky left by the top bar and the check's own card, other than `skip`; at least
 *  `fewest` of them. */
function pickStars(s: AppServices, centre: number, card: ReturnType<typeof cardLayout>, skip: Set<number>, fewest: number): CheckStar[] {
  const e = s.engine, ins = e.insets;
  const W = innerWidth, H = innerHeight;
  const top = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? ins.top + 52;
  // Clear of the edges, with room on the left for each ring's number.
  const region = { x0: ins.left + 40, y0: top + 24, x1: W - ins.right - 24, y1: H - ins.bottom - 24 };
  if (card && card.box.width) {
    if (card.side) region.x1 = Math.min(region.x1, card.box.left - 16);
    else region.y1 = Math.min(region.y1, card.box.top - 16);
  }
  const avoid: { enu: Vec3; radius: number }[] = [];
  for (const b of e.bodies) {
    if (b.id === 'Sun') continue;
    const enu = e.frame.toEnu(b.dir);
    if (b.id === 'Moon') {
      if (enu[2] > -0.05) avoid.push({ enu, radius: 20 });
    } else if (b.mag < 3) avoid.push({ enu, radius: 2 });
  }
  // Well up if the view allows (30°), else a little lower.
  const eye = eyeSkyNow(s);
  for (const minAlt of [30, 20]) {
    const cands = gatherCheckStars({ cat: e.stars, toEnu: (v) => e.frame.toEnu(v), project: (enu) => e.camera.project(enu), region, eye, effRange: [centre - 1.2, centre + 1.2], minAlt, avoid });
    const stars = chooseCheckStars(cands.filter((c) => !skip.has(c.i)), centre, { fewest });
    if (stars.length) return stars;
  }
  return [];
}

/** Why the view had too few stars to ring: low, near the Moon, zoomed in, or just a poor patch of sky. */
function fewReason(s: AppServices): Few {
  const e = s.engine;
  if (e.viewCentre().alt < 25) return 'low';
  const moon = e.bodies.find((b) => b.id === 'Moon');
  if (moon) {
    const m = e.frame.toEnu(moon.dir), f = e.camera.forward();
    if (m[2] > 0 && m[0] * f[0] + m[1] * f[1] + m[2] * f[2] > Math.cos(50 * DEG)) return 'moon';
  }
  if (viewMode.peek() === 'manual' && e.camera.fovDeg < 40) return 'zoom';
  return 'other';
}

/** Stars above the horizon now that the eye sees under sky `eye`, as Naked eye draws them. */
function starsWithin(s: AppServices, eye: EyeSky): number {
  const cat = s.engine.stars, f = s.engine.frame;
  const v: Vec3 = [0, 0, 0];
  let n = 0;
  for (let i = 0; i < cat.count && cat.mag[i] <= eye.max; i++) {
    v[0] = cat.dir[i * 3];
    v[1] = cat.dir[i * 3 + 1];
    v[2] = cat.dir[i * 3 + 2];
    const enu = f.toEnu(v);
    if (enu[2] > 0 && eyeSeesAt(cat.mag[i], enu[0], enu[1], enu[2], eye)) n++;
  }
  return n;
}

const FEW: Record<Few, string> = {
  low: 'Point higher: at least a third of the way up the sky.',
  moon: 'Turn away from the Moon, to a darker part of the sky.',
  zoom: 'Zoom out, so more of the sky is on the screen.',
  other: 'Turn to another part of the sky, away from bright lights, then try again.',
  bright: 'There are no brighter stars to ring in this part of the sky. Turn to another part, with brighter stars, then try again.',
};
const ADVICE = 'Let your eyes adjust first: a few minutes away from bright lights and screens.';
/** What a result means, kept for the place or for tonight only (`about`: how many stars that is, if any). */
const DONE: Record<'kept' | TonightOnly, (about: string) => string> = {
  kept: (about) => `Ahillah now shows just the stars you can see${about}. On other nights it allows for the Moon and twilight by itself.`,
  moon: (about) => `Ahillah shows just those stars tonight${about}. The Moon is too bright tonight to tell how dark the sky is here, so check again on a night without it to keep the result for this place.`,
  twilight: (about) => `Ahillah shows just those stars tonight${about}. Twilight is still too bright to tell how dark the sky is here, so check again later in the night to keep the result for this place.`,
  dark: (about) => `That is more than Ahillah expects anyone to see here tonight, even with no light pollution at all. It shows more stars tonight${about}. Check again on another night to keep a result for this place.`,
  bright: (about) => `That is fewer than even a city centre usually shows: haze or cloud may be about, or your eyes need longer in the dark. Ahillah shows just those stars tonight${about}. Check again on a clearer night to keep a result for this place.`,
};

export function DarkCheck() {
  const s = services.value;
  const session = useRef<Session | null>(null);
  const bounds = useRef<Bounds>(NO_BOUNDS);
  /** Stars already ringed this round, for "Ring other stars". */
  const ringed = useRef(new Set<number>());
  const card = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const rings = useRef<(HTMLButtonElement | null)[]>([]);
  /** A fresh start: no answers kept, and a new session if the sky allows a question now. */
  const freshStep = (): Step => {
    bounds.current = NO_BOUNDS;
    ringed.current.clear();
    const f = s ? firstStep(s) : { step: { k: 'elsewhere' } as Step, moonlit: false };
    const p = place.peek();
    session.current = f.step.k === 'finding' ? { lat: p.lat, lon: p.lon, at: Date.now(), moonlit: f.moonlit } : null;
    return f.step;
  };
  const [step, setStep] = useState<Step>(freshStep);
  /** The step on show, for the listeners set up once: set with it, before any render shows it. */
  const shown = useRef(step);
  shown.current = step;
  const show = (next: Step) => {
    shown.current = next;
    setStep(next);
  };
  const begin = () => show(freshStep());
  /** Put the open question away: its session, answers and stars are no longer for the sky on show. */
  const putAway = (next: Step) => {
    session.current = null;
    bounds.current = NO_BOUNDS;
    ringed.current.clear();
    show(next);
  };
  /** The place has moved on from where a session was asked. */
  const moved = (se: Session) => distanceKm(place.peek(), se) > MOVED_KM;
  useDialog(card, false);
  const close = () => (darkCheckOpen.value = false);

  useEffect(() => {
    // Another card or panel opening ends the check; a tap on the sky meanwhile chooses nothing.
    const offPanel = panel.subscribe((p) => p && close());
    const offSel = selection.subscribe((sel) => sel && queueMicrotask(() => (selection.value = null)));
    // The place, the time on show or a saved night changing puts the open question away (its stars and answers were
    // for the sky as it was), and asks afresh if the sky now allows it.
    const offSky = effect(() => {
      const p = place.value, live = timeLive.value, other = !!previewFrom.value || !!reliving.value;
      const se = session.current;
      if (se && (!live || other || distanceKm(p, se) > MOVED_KM)) untracked(begin);
    });
    // In the background (a phone put away, even overnight) the question is put away too, and asked afresh on return,
    // from the sky as the engine next draws it (not the sky left behind).
    const onVisible = () => {
      const k = shown.current.k;
      if (document.visibilityState === 'hidden') {
        if (k === 'finding' || k === 'ask' || k === 'north' || k === 'away') putAway({ k: 'away' });
      } else if (k === 'away') begin();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Turned on its side, or a new window size: the card may have moved, so the round's stars are chosen again.
    let size = `${innerWidth}x${innerHeight}`;
    let wait: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(wait);
      wait = setTimeout(() => {
        const st = shown.current;
        if (`${innerWidth}x${innerHeight}` === size) return;
        size = `${innerWidth}x${innerHeight}`;
        if (st.k !== 'ask' && st.k !== 'finding') return;
        ringed.current.clear();
        show({ k: 'finding', round: st.round, centre: st.centre });
      }, 250);
    };
    addEventListener('resize', onResize);
    return () => {
      offPanel();
      offSel();
      offSky();
      document.removeEventListener('visibilitychange', onVisible);
      removeEventListener('resize', onResize);
      clearTimeout(wait);
    };
  }, []);

  // The stars for a round are chosen once the card is up, so they keep clear of it.
  useLayoutEffect(() => {
    if (step.k !== 'finding' || !s) return;
    const se = session.current;
    // Before stars are chosen (a new round, or other stars): the sky must still allow a question.
    const why = notNow(s);
    if (why) return putAway(why);
    if (!se || moved(se)) return begin();
    const box = cardLayout(card.current);
    // A second round makes do with three stars: the brightest are few.
    const fewest = step.round > 1 ? 3 : 4;
    // Other stars if there are any; the same ones again if not.
    let stars = pickStars(s, step.centre, box, ringed.current, fewest);
    if (!stars.length && ringed.current.size) {
      ringed.current.clear();
      stars = pickStars(s, step.centre, box, ringed.current, fewest);
    }
    stars.forEach((x) => ringed.current.add(x.i));
    if (stars.length) show({ k: 'ask', round: step.round, centre: step.centre, stars, session: se, at: Date.now() });
    // A second round with nothing fainter to ring: the faintest seen is the answer. With nothing brighter: say so.
    else if (step.round > 1 && bounds.current.seen != null) settled(settle(bounds.current, ROUNDS));
    else show({ k: 'few', why: step.round > 1 ? 'bright' : fewReason(s) });
  }, [step]);

  // The rings follow their stars as the view moves (by finger or by pointing), while the view can be trusted.
  useEffect(() => {
    if (step.k !== 'ask' || !s) return;
    const e = s.engine;
    let raf = 0;
    const frame = () => {
      // Pointing, and the compass no longer knows north (back from the background, say): the rings would not match the
      // real sky, so the round is put away until it does.
      if (!pointingReady(s)) return putAway({ k: 'north' });
      step.stars.forEach((st, k) => {
        const el = rings.current[k];
        if (!el) return;
        const enu = e.frame.toEnu(e.starDir(st.i));
        const p = enu[2] > -0.02 ? e.camera.project(enu) : null;
        const on = !!p && e.camera.onScreen(p, -6);
        if (el.hidden === on) el.hidden = !on;
        if (on) el.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
      });
      raf = requestAnimationFrame(frame);
    };
    frame();
    return () => cancelAnimationFrame(raf);
  }, [step]);

  // Pointing: the question is asked once the compass knows north (or the person changes to dragging the sky).
  useEffect(() => {
    if (step.k !== 'north' || !s) return;
    const id = setInterval(() => pointingReady(s) && begin(), 300);
    return () => clearInterval(id);
  }, [step]);

  // Put away: asked afresh once the engine has drawn a frame of this place and moment, watched frame by frame (never
  // while hidden, and no longer than the wait lasts).
  useEffect(() => {
    if (step.k !== 'away' || !s || document.visibilityState === 'hidden') return;
    let raf = 0;
    const tick = () => {
      if (notNow(s)?.k === 'away') raf = requestAnimationFrame(tick);
      else begin();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step]);

  // Each new question or answer is read out first: the focus goes to its heading.
  useEffect(() => title.current?.focus({ preventScroll: true }), [step.k, 'round' in step ? step.round : 0]);

  // Escape closes; on a keyboard, the number keys answer.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        return close();
      }
      if (step.k === 'ask' && /^[1-9]$/.test(e.key) && +e.key <= step.stars.length && !(e.target instanceof HTMLInputElement)) answer(+e.key - 1);
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [step]);

  if (!s) return null;

  function settled(st: Settled) {
    if ('limit' in st) finish(st.limit);
    else if ('again' in st) {
      ringed.current.clear();
      show({ k: 'finding', round: (step as { round: number }).round + 1, centre: st.centre });
    }
    else putAway({ k: 'fail' });
  }
  function answer(a: Answer) {
    // A question already put away (another place, another time, the background): nothing to do.
    if (step.k !== 'ask' || step.session !== session.current) return;
    // The sky no longer allows one (not dark now, off the live sky, not yet drawn, north lost): nothing is written.
    const why = notNow(s!);
    if (why) return putAway(why);
    // Out of date, or moved: nothing is written, and the question starts again.
    if (moved(step.session) || Date.now() - step.at > STALE_MS) return begin();
    // The stars as the eye sees them now, a little higher or lower than when they were ringed. Should their order have
    // changed, they are no longer a scale to answer on: ring them again.
    const e = s!.engine, eye = eyeSkyNow(s!);
    const effs = step.stars.map((st) => eyeMag(st.mag, e.frame.toEnu(e.starDir(st.i)), eye));
    if (effs.some((m, k) => k > 0 && m <= effs[k - 1])) return show({ k: 'finding', round: step.round, centre: step.centre });
    bounds.current = applyAnswer(bounds.current, effs, a);
    settled(settle(bounds.current, step.round));
  }
  function finish(found: number) {
    const se = session.current;
    // Written only for the session it was asked in, while the sky still allows it: else nothing is written.
    const why = notNow(s!);
    if (why) return putAway(why);
    if (!se || moved(se)) return begin();
    // Kept for the place when it tells the light pollution; for tonight only when it cannot, or under a bright Moon.
    const r = judgeCheck(found, skyNow(s!));
    const tonight = se.moonlit ? r.tonight ?? 'moon' : r.tonight;
    markOffered(se);
    if (tonight) nudgeTonightTo(found, baseLimitNow(s!), se);
    else saveCheck({ lat: se.lat, lon: se.lon, sqm: r.sqm, limit: r.limit, t: Date.now() });
    session.current = null;
    // What Ahillah shows now: the light pollution just kept, or tonight's nudge (which only goes so far).
    show({ k: 'done', found, count: starsWithin(s!, eyeSkyOf(s!, skyNow(s!), limitNudge.peek())), tonight });
  }

  const head = (text: string) => <h2 id="dc-title" ref={title} tabIndex={-1}>{text}</h2>;
  let body;
  switch (step.k) {
    case 'finding':
    case 'ask': {
      const stars = step.k === 'ask' ? step.stars : null;
      const n = stars?.length ?? 5;
      body = (
        <>
          {head('Which is the faintest one you can see?')}
          <p class="dc-lead">
            {step.round > 1 ? `Now these, ${bounds.current.seen != null ? 'a little fainter' : 'a little brighter'}. ` : 'Look for the ringed stars in the sky. '}
            {session.current?.moonlit ? 'With the Moon this bright, your answer counts for tonight only. ' : ''}
            {ADVICE}
          </p>
          <div class="dc-numbers" role="group" aria-label="The ringed stars, brightest first">
            {Array.from({ length: n }, (_, k) => (
              <button
                key={k} class="dc-num" disabled={!stars} onClick={() => answer(k)}
                aria-label={stars ? `Star ${k + 1}${k === 0 ? ', the brightest' : k === n - 1 ? ', the faintest' : ''}: ${whereInSky(stars[k].alt, stars[k].az).toLowerCase()}` : `Star ${k + 1}`}
              >
                {k + 1}
              </button>
            ))}
          </div>
          <div class="dc-ends" aria-hidden="true"><span>Brightest</span><span>Faintest</span></div>
          <div class="dc-row">
            <button class="btn-ghost" disabled={!stars} onClick={() => answer('none')}>None of them</button>
            <button class="btn-ghost" disabled={!stars} onClick={() => answer('all')}>All of them</button>
          </div>
          <button class="dc-other" disabled={!stars} onClick={() => show({ k: 'finding', round: step.round, centre: step.centre })}>
            Ring other stars
          </button>
        </>
      );
      break;
    }
    case 'done': {
      const about = step.count > 0 ? `: about ${step.count > 50 ? Math.round(step.count / 10) * 10 : step.count} above you now` : '';
      body = (
        <>
          <span class="dc-done-icon" aria-hidden="true"><Icon name="check" size={20} stroke={2.2} /></span>
          {head(`You can see stars to magnitude ${fmtNum(step.found, 1)} ${step.tonight ? 'tonight' : 'here'}`)}
          <p class="dc-lead">{DONE[step.tonight ?? 'kept'](about)}</p>
          {/* A sky full of stars (item 43). */}
          <Quote id="Q-47" />
          <div class="dc-row"><button class="btn" onClick={close}>Done</button></div>
        </>
      );
      break;
    }
    case 'few':
      body = (
        <>
          {head('Not enough stars to ring here')}
          <p class="dc-lead">{FEW[step.why]}</p>
          <div class="dc-row">
            <button class="btn-ghost" onClick={close}>Close</button>
            <button class="btn" onClick={begin}>Try again</button>
          </div>
        </>
      );
      break;
    case 'early':
      body = (
        <>
          {head('It is not dark enough yet')}
          <p class="dc-lead">
            Twilight still lights the sky, so the check cannot tell how dark it is here.
            {step.at ? ` Try again after ${fmtTime(step.at)}.` : ' The Sun stays too close below the horizon tonight for the sky here to get dark enough.'}
          </p>
          <div class="dc-row"><button class="btn-ghost" onClick={close}>Close</button></div>
        </>
      );
      break;
    case 'north':
      body = (
        <>
          {head('Finding north…')}
          <p class="dc-lead">Point the phone at the horizon for a moment. The stars to look for are ringed once the compass knows north.</p>
          <div class="dc-row"><button class="btn-ghost" onClick={close}>Close</button></div>
        </>
      );
      break;
    case 'away':
      body = head('One moment…');
      break;
    case 'elsewhere':
      body = (
        <>
          {head('The check needs tonight’s sky, here')}
          <p class="dc-lead">Ahillah is showing another time or place. Go back to now, where you are, and try again.</p>
          <div class="dc-row">
            <button class="btn-ghost" onClick={close}>Close</button>
            <button class="btn" onClick={() => { endPreview(); goLive(); setTimeout(begin, 400); }}>Back to now</button>
          </div>
        </>
      );
      break;
    case 'fail':
      body = (
        <>
          {head('Not even the brightest ones?')}
          <p class="dc-lead">Cloud, haze or mist may be in the way, or your eyes need longer in the dark. Nothing has changed. Try again later.</p>
          <div class="dc-row">
            <button class="btn-ghost" onClick={close}>Close</button>
            <button class="btn" onClick={begin}>Try again</button>
          </div>
        </>
      );
      break;
  }

  return (
    <>
      <div class="dc-card" ref={card} role="dialog" aria-labelledby="dc-title" tabIndex={-1}>
        <button class="dc-close" onClick={close} aria-label="Close the darkness check"><Icon name="close" size={13} stroke={2.6} /></button>
        {body}
      </div>
      {step.k === 'ask' && (
        <div class="dc-rings">
          {step.stars.map((st, k) => (
            <button
              key={`${step.round}-${st.i}`} class="dc-ring" hidden ref={(el) => void (rings.current[k] = el)} onClick={() => answer(k)}
              aria-label={`Star ${k + 1}, ${whereInSky(st.alt, st.az).toLowerCase()}: the faintest I can see`}
            >
              <span class="dc-ring-dot" aria-hidden="true" />
              <span class="dc-ring-n" aria-hidden="true">{k + 1}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- The offer ----------
const OFFER = {
  first: { title: 'How many stars can you see here?', text: 'A 10-second check: Ahillah rings a few real stars and you tap the faintest you can see. Then it shows just the stars your eyes can see from here.' },
  again: { title: 'Seeing fewer stars than Ahillah shows?', text: 'A 10-second check of the faintest star you can see sets Ahillah to your eyes, here.' },
};
/** The line that says why the check is offered again (offerAgainWhy). */
export const againLine = (w: AgainWhy) =>
  'taps' in w ? `Asking again because you tapped “I can’t see it” ${w.taps === 2 ? 'twice' : `${w.taps} times`} tonight.`
  : 'km' in w ? `Asking again because you are ${fmtNum(w.km, w.km < 10 ? 1 : 0)} km from your last check.`
  : 'Asking again because each place has its own check.';

/** The offer is on screen (Help's own offer to a new user waits for it). */
export const darkOfferShown = signal(false);

/** Something else is on screen that the offer must not cover: a sheet or card, a film, What to show, the iPhone's
 *  motion card, Help's offer while it shows, lining up with the sky or its offer, or the check itself. */
const busyNow = () =>
  panel.value !== null || !!selection.value || introOpen.value || calendarFilmOpen.value || !!storyOpen.value || whatToShow.value || pointingPrompt.value
  || guideOffer.value === 'shown' || darkCheckOpen.value || onceOfferShown.value || !!aligning.value || alignOfferShown.value;

/** Whether the check is due to be offered now ('first' or 'again'), from the sky on show and what is on screen. */
function dueNow(blocked: boolean): 'first' | 'again' | null {
  const s = services.value;
  // Not until the sky has been worked out once (before its first frame there is no Sun to tell the time by), nor while
  // pointing with the compass still finding north.
  if (!s || !s.engine.bodies.length || !hereAndNow() || !pointingReady(s)) return null;
  const sky = skyNow(s), p = place.value, taps = cantSeeTaps.value;
  return offerDue({
    nakedEye: settings.value.nakedEye, hereAndNow: true, sunAlt: sky.sunAlt,
    tooBright: moonTwilightShare(sky, sqmForLimit(baseLimitNow(s), sky)) > MOON_TWILIGHT_MAX,
    checkedHere: !!checkAt(p, skyChecks.value), offeredHere: offeredAt(p, offeredPlaces.value),
    cantSeeTaps: taps, reofferedTonight: taps > 0 && !!eyeTonight.value?.reoffered, busy: blocked || busyNow(),
  });
}

/** The offer of the darkness check, once per place, the first time Ahillah is used there after dark (mounted in App). */
export function DarkCheckOffer({ blocked }: { blocked: boolean }) {
  // Looked at again every 2 s: the Sun sets and the sky's first positions arrive without any other change to react to.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 2000);
    return () => clearInterval(id);
  }, []);
  const [shown, setShown] = useState<'first' | 'again' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const blockedNow = useRef(blocked);
  blockedNow.current = blocked;
  const due = dueNow(blocked);
  // A quiet moment first: offered once it has been due for a few seconds, and still is. Shown counts as offered, so a
  // reload before an answer does not offer it again.
  useEffect(() => {
    if (!due || shown) return;
    const id = setTimeout(() => {
      const d = dueNow(blockedNow.current);
      if (!d) return;
      setShown(d);
      if (d === 'first') markOffered(place.peek());
      else markReoffered(place.peek());
    }, 4000);
    return () => clearTimeout(id);
  }, [due, shown]);
  const s = services.value;
  const dark = !!s && s.engine.bodies.length > 0 && skyNow(s).sunAlt <= DARK_ENOUGH;
  const visible = !!shown && !blocked && !busyNow() && hereAndNow() && settings.value.nakedEye && dark
    && (shown === 'first' ? !checkAt(place.value, skyChecks.value) : cantSeeTaps.value >= 2);
  useEffect(() => {
    darkOfferShown.value = visible;
    return () => void (darkOfferShown.value = false);
  }, [visible]);
  const later = () => {
    setShown(null);
    showToast('The darkness check is in Sky view whenever you want it', 4500);
    // The focus goes to where the check lives, as Help's offer sends it to Help.
    setTimeout(() => [...document.querySelectorAll<HTMLElement>('.dock .dock-btn')].find((b) => b.textContent?.trim() === 'Sky view')?.focus({ preventScroll: true }), 0);
  };
  const yes = () => {
    setShown(null);
    startDarkCheck();
  };
  useEffect(() => {
    if (!visible) return;
    if (!focused.current) {
      focused.current = true;
      ref.current?.focus({ preventScroll: true });
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      later();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [visible]);
  if (!visible || !shown) return null;
  const o = OFFER[shown];
  // Asked before (tonight, or at another place): one line on why it is asked again.
  const why = offerAgainWhy(shown, place.value, cantSeeTaps.value, skyChecks.value, offeredPlaces.value);
  return (
    <div class="dc-offer" role="dialog" aria-labelledby="dc-offer-title" aria-describedby={why ? 'dc-offer-why dc-offer-text' : 'dc-offer-text'} tabIndex={-1} ref={ref}>
      <h2 id="dc-offer-title"><span class="dc-offer-icon" aria-hidden="true"><Icon name="eye" size={18} /></span>{o.title}</h2>
      {why && <p class="dc-offer-why" id="dc-offer-why">{againLine(why)}</p>}
      <p id="dc-offer-text">{o.text}</p>
      <div class="dc-row">
        <button class="btn-ghost" onClick={later}>Not now</button>
        <button class="btn" onClick={yes}>Check now</button>
      </div>
    </div>
  );
}

// ---------- In Sky view ----------
/** When a check was made, in words: tonight (or last night, once it is light), or the day. */
const whenWords = (t: number, lon: number) => (nightId(t, lon) === nightId(Date.now(), lon) ? (darkOut.value ? 'tonight' : 'last night') : `on ${fmtDate(t)}`);

/** In Sky view, under the Naked eye switch: the way into the darkness check, the last result here, and "I can see more
 *  stars than this". */
export function DarkCheckEntry() {
  const set = settings.value;
  const p = place.value;
  const c = checkAt(p, skyChecks.value);
  const nudge = limitNudge.value;
  if (!set.nakedEye) return null;
  // Back to the light pollution map's estimate here, as before any check.
  const forget = () => {
    const undo = forgetCheck(place.peek());
    if (undo) showToast('Forgotten: Ahillah uses the light pollution map here again', 6000, { label: 'Undo', run: undo });
  };
  const more = () => {
    const s = services.peek();
    if (!c || !hereAndNow() || !s) return startDarkCheck();
    const undo = eyeLimitNow(s) < EYE_MAX - 0.05 ? seeMore(place.peek()) : null;
    if (undo) showToast('Showing fainter stars tonight', 6000, { label: 'Undo', run: undo });
    else showToast('Ahillah already shows as many stars as it can for tonight', 4500);
  };
  return (
    <div class="dc-entry">
      <div class="dc-entry-text">
        <span class="row-label-inline">Darkness check</span>
        <span class="row-sub">
          {c
            ? `From your darkness check: you can see stars to magnitude ${fmtNum(c.limit, 1)} here (checked ${whenWords(c.t, c.lon)}).`
            : 'Not checked here yet. In 10 seconds it sets Ahillah to the faintest stars your eyes can see from here.'}
        </span>
        {nudge !== 0 && <span class="row-sub">Tonight Ahillah shows {nudge < 0 ? 'fewer' : 'more'} stars, as you said.</span>}
      </div>
      <div class="dc-entry-actions">
        <button class="btn-ghost" onClick={startDarkCheck}><Icon name="eye" size={17} /> {c ? 'Check again' : 'Check now'}</button>
        <button class="btn-ghost" onClick={more}>I can see more stars than this</button>
        {c && <button class="btn-ghost" onClick={forget}>Forget this check</button>}
        {nudge !== 0 && <button class="btn-ghost" onClick={resetNudge}>Undo tonight’s change</button>}
      </div>
    </div>
  );
}

/** "I can't see it" on a card, for something predicted visible: tonight's limit goes to just brighter than it. */
export function cantSeeIt(s: AppServices, eff: number, name: string) {
  const r = cantSee(eff, baseLimitNow(s), place.peek());
  if (!r.nudged) return void showToast('Something this bright is hard to miss: cloud, haze or something nearby may be in the way', 6000);
  showToast(`Showing only what is brighter than ${name} tonight`, 6000, { label: 'Undo', run: r.undo });
  // The button goes with the change it made: the focus moves to the card now, rather than fall to the page then.
  document.querySelector<HTMLElement>('.sheet.info')?.focus({ preventScroll: true });
}
